/**
 * Croatian overrides for the submissions screens.
 *
 * Only what genuinely differs from Bosnian. Croatian is ijekavica too, so
 * nothing moves for the jat reflex — what changes is vocabulary: *sustav*,
 * *tvrtka*, *datoteka*, *sigurnost*, *tipka*, and borrowed verbs in -irati.
 *
 * An entry identical to its Bosnian base is a bug, not a no-op: i18n.test.ts
 * fails on one, because a line that changes nothing is a decision that was
 * reverted in bs.ts without anybody revisiting this file.
 */

import type { SubmissionsSlice } from '../bs/submissions'

export const submissions: Partial<SubmissionsSlice> = {
  // ---- Shared by more than one screen ------------------------------
  // *tko* and *netko*, which is the only thing that moves in this sentence —
  // the four status names inside it are the server's and arrive translated.
  'submissions.status.chain.hint':
    '„{new}” čeka da je netko preuzme, „{inProgress}” da se na njoj radi, „{resolved}” da je posao gotov i čeka potvrdu, a „{closed}” da je prijava završena i da se više ne mijenja — status mijenja nadležni, ne onaj tko je prijavio.',

  // ---- The list ----------------------------------------------------
  // *filtar* declines as *filtri* / *filtre*, not *filteri* / *filtere*.
  'submissions.list.filters': 'Filtri',
  'submissions.list.empty.description': 'Promijenite filtre ili napravite novu prijavu.',

  // ---- One submission ----------------------------------------------
  // *ispis* rather than *štampa*, *izvješće* rather than *izvještaj*, and
  // *povijest* rather than *istorija* — the three words on this screen a
  // Croatian reader would notice immediately.
  'submissions.detail.print': 'Ispiši',
  'submissions.detail.detailedReport': 'Detaljno izvješće',
  'submissions.detail.history': 'Povijest',

  // *pomicati* rather than *pomjerati*, and *poslana* rather than *poslata* —
  // the same participle the offline strings below already use.
  'submissions.detail.field.due.hint':
    'Rok se računa sam, prema prioritetu u trenutku prijave — kasnija promjena prioriteta ga ne pomiče.',
  'submissions.detail.field.resolvedIn.hint':
    'Sati od trenutka kada je prijava poslana do trenutka kada je označena kao riješena — računa se stvarno vrijeme, a ne radni sati.',

  'submissions.detail.assign.hint':
    'Dodjeljivanje nove prijave je istodobno prebacuje u sljedeći status.',
  // *povjerenstvo* again, matching the reward strings further down.
  'submissions.detail.assign.who':
    'Prijavu dodjeljuju nadležni, povjerenstvo i administrator; održavanje je rješava, ali je ne može prebaciti na nekoga drugog.',
  'submissions.detail.assign.none': 'Nitko',

  // *povijest* and *obavijest*, the two words this sentence turns on.
  'submissions.detail.status.commit':
    'Status mijenja samo nadležni, svaka promjena upisuje se u povijest s vašim imenom i vremenom, a onaj tko je prijavio dobiva obavijest.',
  'submissions.detail.status.choose': 'Odaberite',
  'submissions.detail.status.note.placeholder': 'Neobvezno — zašto status mijenjate.',
  'submissions.detail.status.save': 'Spremi status',

  // *datoteka* for the file, *poslužitelj* for the server — the same pair the
  // shared network errors in hr/common.ts use.
  'submissions.detail.delete.attachments.one':
    '{count} prilog — datoteke se brišu i s poslužitelja',
  'submissions.detail.delete.attachments.few':
    '{count} priloga — datoteke se brišu i s poslužitelja',
  'submissions.detail.delete.attachments.many':
    '{count} priloga — datoteke se brišu i s poslužitelja',
  'submissions.detail.delete.history': 'cijela povijest statusa',
  'submissions.detail.delete.audit':
    'Tko je i kada obrisao ostaje zabilježeno u dnevniku aktivnosti.',

  // ---- The improvement block ---------------------------------------
  'submissions.improvement.expectedImpact': 'Očekivani učinak',

  // ---- The form ----------------------------------------------------
  'submissions.form.subtitle':
    'Možete poslati i samo glasovnu poruku, fotografiju ili video — opis nije obvezan.',
  'submissions.form.help':
    'Prijavu možete popuniti i kada nema signala — čuva se na telefonu i pošalje se sama čim se veza vrati, pa možete slobodno zatvoriti aplikaciju. Svaka poslana prijava dobije svoj broj (npr. PR-2026-0042) koji možete izgovoriti ili napisati na radni nalog.',
  // *sigurnosni*, not *bezbjednosni* — the one word on this screen a Croatian
  // reader would stumble over.
  'submissions.form.type.hint':
    'Kvar, incident i sigurnosni problem idu nadležnom da ih dodijeli na rješavanje, a prijedlog za poboljšanje otvara dodatna polja i ide na ocjenu koja može donijeti nagradu.',
  'submissions.form.workCenter.choose': 'Odaberite radni centar',
  // *stroj* and *popis*, as in the scanner strings in hr/tasks.ts.
  'submissions.form.workCenter.hint':
    'Ako stroj ima QR naljepnicu, skenirajte je umjesto da tražite centar u popisu — radni centar se tada popuni sam.',
  'submissions.form.description.placeholder':
    'Što se događa, od kada, i što ste do sada probali.',
  'submissions.form.improvement.proposal.placeholder': 'Što konkretno promijeniti.',
  'submissions.form.improvement.expectedImpact.placeholder':
    'Ušteda vremena, manje škarta, sigurnije radno mjesto…',
  'submissions.form.improvement.duration': 'Trajanje provedbe',
  'submissions.form.improvement.duration.placeholder': 'npr. 2 tjedna',
  // *provesti* rather than *sprovesti*, matching *provedba* above.
  'submissions.form.improvement.estimate.hint':
    'Ovo je procjena — stvarni iznos uštede i troška nadležni upisuje kasnije, kad se prijedlog provede.',
  'submissions.form.voice.hint':
    'Ispričajte što ne valja umjesto da tipkate. Poruku šaljemo zajedno s prijavom.',
  // *utipkati*, for the same reason the sentence above says *tipkate*.
  'submissions.form.voice.explain':
    'Ako vam je lakše reći nego utipkati, snimite jednu poruku — nadležni je sluša na samoj prijavi.',
  'submissions.form.media.hint':
    'Fotografija je ono što prijavu čini rješivom — onaj tko dolazi na popravak vidi kvar prije nego krene i zna što ponijeti.',

  // *spremiti* rather than *sačuvati*, and the future written apart:
  // *bit će poslana*, not *biće poslata*.
  'submissions.form.saveToDevice': 'Spremi na uređaj',
  'submissions.form.offlineNotice':
    'Trenutačno nema veze s poslužiteljem. Prijava se sprema na uređaj i bit će poslana automatski čim se veza vrati — možete slobodno zatvoriti aplikaciju.',
  'submissions.form.needContent.toast':
    'Dodajte opis, glasovnu poruku ili prilog prije spremanja.',
  'submissions.form.savedOffline':
    'Nema veze — prijava je spremljena na uređaju i poslat će se automatski.',
  'submissions.form.savedAfterFailure':
    'Slanje nije uspjelo, ali je prijava spremljena i bit će poslana automatski.',
  'submissions.form.saveFailed': 'Spremanje na uređaj nije uspjelo.',
  'submissions.form.sent': 'Prijava {reference} je poslana.',

  // ---- Tasks -------------------------------------------------------
  'submissions.tasks.empty.manager':
    'Nema zadataka. Dodijelite zadatak da bi netko preuzeo rješavanje.',
  // *izvršitelj*, not *izvršilac* — the -telj agent noun, as everywhere in hr.
  'submissions.tasks.help':
    'Kad rješavanje ima više koraka, napravite zadatak za svaki — svaki ima svog izvršitelja i svoj rok, i svaki se posebno zatvara. Prijava ostaje otvorena dok je vi ne prebacite dalje, bez obzira na to koliko je zadataka završeno.',
  // The -irati borrowing, which is the whole rule in one word.
  'submissions.tasks.verify': 'Verificiraj',
  'submissions.tasks.form.assignee.choose': 'Odaberite osobu',
  'submissions.tasks.form.description.placeholder':
    'Neobvezno — što točno treba napraviti, koji dijelovi su potrebni.',

  // ---- The reward scheme -------------------------------------------
  // *povjerenstvo* is the committee; *komisija* is not the word here.
  'submissions.reviews.empty.manager':
    'Još nema ocjena. Nadležni daje prvu ocjenu, povjerenstvo donosi konačnu odluku i nagradu.',
  'submissions.reviews.awardField.hint': 'Samo povjerenstvo može dodijeliti novčanu nagradu.',
  'submissions.reviews.points.hint':
    'Bodovi se dodjeljuju samo prijedlozima za poboljšanje, po tri kriterija, svaki 0–5.',
  'submissions.reviews.points.sum': 'Zbroj bodova',
  'submissions.reviews.points.override': 'Korigirani ukupan broj',
  'submissions.reviews.points.override.hint': 'Ostavite prazno da se koristi zbroj.',
  'submissions.reviews.save': 'Spremi ocjenu',
  'submissions.reviews.saved': 'Ocjena je spremljena.',

  // ---- Spoken messages ---------------------------------------------
  // *snimka* is feminine here, so the whole sentence moves with it, and
  // Croatian prefers the infinitive to the *da*-clause: *ne može
  // reproducirati*, not *ne može da pusti*.
  'submissions.voice.again': 'Snimi ponovno',
  'submissions.voice.empty': 'Snimka je prazna. Provjerite mikrofon i pokušajte ponovno.',
  'submissions.voice.play': 'Reproduciraj {label}',
  'submissions.voice.position': 'Pozicija u snimci — {label}',
  'submissions.voice.unsupported':
    'Ovaj preglednik ne može reproducirati ovaj format — preuzmite snimku.',

  // ---- Who was working the centre ----------------------------------
  'submissions.workers.choose': 'Odaberite radnike',
  'submissions.workers.clear': 'Poništi odabir',
  'submissions.workers.hint':
    'Tko je radio na ovom centru u razdoblju koje ocjenjujete. Ocjenjuje se radni centar, ne radnik — ovo je podatak o tome tko ga je održavao.',
  'submissions.workers.unavailable': 'Popis nije dostupan',
  'submissions.edit.closed':
    'Ova se prijava više ne može mijenjati — netko ju je već preuzeo ili je zatvorena. Dodajte komentar ili zamolite odgovornu osobu.',

  // ---- The list as queues ------------------------------------------
  // *netko* and *nitko*, and *filtri* as above.
  'submissions.tabs.hint':
    '„U radu” su sve otvorene prijave koje je netko preuzeo. „Riješene” čekaju da ih nadležni potvrdi i zatvori.',
  'submissions.filter.clear': 'Očisti filtre',
  'submissions.card.nobody': 'Nitko nije zadužen',
  'submissions.empty.nove': 'Ovdje stižu nove prijave dok ih nitko ne preuzme.',
  'submissions.empty.u-radu': 'Ovdje su prijave koje je netko preuzeo, a još nisu riješene.',
  'submissions.empty.filtered.title': 'Ništa ne odgovara filtrima',
  'submissions.empty.filtered': 'Uklonite neki od filtara iznad ili pogledajte drugu karticu.',

  // ---- One report --------------------------------------------------
  'submissions.next.reconsider': 'Ponovno razmotri',
  'submissions.next.reason.hint': 'Onaj tko je prijavio vidi ovaj razlog.',
  'submissions.next.reason.close.placeholder': 'npr. Ležaj zamijenjen, stroj radi normalno.',
  // *poslana*, as everywhere else in this file.
  'submissions.history.created': 'Prijava je poslana',

  // ---- The new-report form -----------------------------------------
  'submissions.form.machine.search': 'Traži stroj',
  'submissions.form.machine.heading': 'Na kojem stroju?',
  'submissions.form.machine.yours': 'Vaši strojevi',
  'submissions.form.machine.none': 'Nijedan stroj ne odgovara pretrazi.',
  'submissions.form.machine.required': 'Odaberite stroj.',
  'submissions.form.machine.scanTitle': 'Skenirajte QR naljepnicu na stroju',
  'submissions.form.machine.scanFailed':
    'Ovaj QR kod ne pripada nijednom stroju koji vidite. Odaberite ga s popisa.',
  'submissions.form.machine.scanOffline':
    'Za QR je potrebna veza. Odaberite stroj s popisa — prijava se i dalje čuva na uređaju.',
  'submissions.form.machine.unknown': 'Stroj iz poveznice nije pronađen — odaberite ga s popisa.',
  'submissions.filter.workCenter': 'Stroj',
  'submissions.col.machine': 'Stroj · Pogon',
}
